import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import api from "../api";
import { ITiCloudConfig, IUseLicensing } from "../@types/licensing";
import { show } from "../../../notifications/notifications";
import { NotificationsLevel } from "../../../lib/enums";
import { getLocalLicenseQueryKey } from "../queryKeys";
import useLocalLicense from "./useLocalLicense";
import { ErrorResponse, MessageErrorResponse } from "../../../requests/@types";

const REFRESH_AFTER_CLOUD_CONFIG_SECONDS = 10;
/**
 * Returns local license and services to regenerate licensing ID and upload license file.
 * @returns IUseLicensing
 */
const useLicensing = (): IUseLicensing => {
  const queryClient = useQueryClient();

  const invalidateLocalLicense = () => {
    queryClient.invalidateQueries({ queryKey: getLocalLicenseQueryKey });
  };

  const {
    localLicense,
    isLocalLicenseLoading,
    isLocalLicenseExpiring,
    isLocalLicenseValidOrOnline
  } = useLocalLicense();

  const { mutate: generateMachineId, isPending: isGenerateMachineIdLoading } =
    useMutation({
      mutationFn: (machineId: string) => api.generateMachineId(machineId),
      onSuccess: invalidateLocalLicense,
      onError: () => {
        show({
          level: NotificationsLevel.ERROR,
          message: "Error while generating licensing ID..."
        });
      }
    });

  const { mutate: uploadLicenseFile, isPending: isUploadLicensingFileLoading } =
    useMutation({
      mutationFn: (licensingFile: File) => api.uploadLicenseFile(licensingFile),
      onSuccess: data => {
        invalidateLocalLicense();

        show({
          level: NotificationsLevel.SUCCESS,
          message: data.data?.message ?? "License uploaded successfully."
        });
      },
      onError: (error: ErrorResponse<MessageErrorResponse>) => {
        show({
          level: NotificationsLevel.ERROR,
          message: error?.data?.message ?? "Error while uploading license..."
        });
      }
    });

  const {
    data: tiCloudConfigData,
    error: tiCloudConfigError,
    isPending: isTiCloudConfigLoading,
    isError: isTiCloudConfigError
  } = useQuery<
    AxiosResponse<ITiCloudConfig>,
    ErrorResponse<MessageErrorResponse>
  >({
    queryKey: ["tiCloudConfig"],
    queryFn: api.getTiCloudConfig
  });

  useEffect(() => {
    if (isTiCloudConfigError) {
      show({
        level: NotificationsLevel.ERROR,
        message:
          tiCloudConfigError?.data?.message ?? "Error while fetching license..."
      });
    }
  }, [isTiCloudConfigError, tiCloudConfigError?.data?.message]);

  const {
    mutate: configureTiCloudMutation,
    isPending: isConfigureTiCloudLoading,
    isSuccess: isConfigureTiCloudSuccessful
  } = useMutation({
    mutationFn: (formValues: Record<string, string>) => {
      const newConfig = {
        url: formValues?.titaniumCloudUrl,
        user: formValues?.username,
        token: formValues?.password,
        proxy_host: formValues?.proxyHost,
        proxy_port: formValues?.proxyPort,
        proxy_user: formValues?.proxyUsername,
        proxy_password: formValues?.proxyPassword,
        timeout: formValues?.requestTimeout
      };

      return api.configureTiCloud(newConfig);
    },
    onSuccess: () => {
      show({
        level: NotificationsLevel.SUCCESS,
        message: `Spectra Intelligence successfully configured. The page will be refreshed in ${REFRESH_AFTER_CLOUD_CONFIG_SECONDS} seconds.`
      });

      setTimeout(() => {
        location.reload();
      }, REFRESH_AFTER_CLOUD_CONFIG_SECONDS * 1000);
    },
    onError: (data: ErrorResponse<MessageErrorResponse>) => {
      show({
        level: NotificationsLevel.ERROR,
        message:
          data?.data?.message ??
          "Error while configuring Spectra Intelligence..."
      });
    }
  });

  const getTiCloudConfigValues = () => ({
    titaniumCloudUrl: tiCloudConfigData?.data?.url,
    username: tiCloudConfigData?.data?.user,
    requestTimeout: tiCloudConfigData?.data?.timeout,
    proxyHost: tiCloudConfigData?.data?.proxy_host,
    proxyPort: tiCloudConfigData?.data?.proxy_port,
    proxyUsername: tiCloudConfigData?.data?.proxy_user
  });

  return {
    localLicense,
    isLocalLicenseExpiring,
    isLocalLicenseValidOrOnline,
    generateMachineId,
    configureTiCloud: configureTiCloudMutation,
    tiCloudConfig: getTiCloudConfigValues(),
    uploadLicenseFile,
    isLoading:
      isLocalLicenseLoading ||
      isGenerateMachineIdLoading ||
      isUploadLicensingFileLoading ||
      isTiCloudConfigLoading ||
      isConfigureTiCloudLoading ||
      isConfigureTiCloudSuccessful
  };
};

export default useLicensing;
